import {
   ChangeDetectionStrategy,
   Component,
   computed,
   inject,
   OnDestroy,
   signal
} from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { AuthService, User as Auth0User } from '@auth0/auth0-angular';
import { environment } from '../../../../../environments/environment';

import { PropertyStoreService } from '../../../../services/stores/property-store.service';
import { PropertyService } from '../../../../services/property.service';
import { Property } from '../../../../domain/property';
import { User } from '../../../../domain/user';
import { RoleLabelPipe } from '../../shared/role-label.pipe';
import { Subject, takeUntil } from 'rxjs';

const ACCOUNT_OWNER_ROLE_ID = environment.roles.accountOwner;
const SUPPORT_HUB_LINK = environment.externalLinks.supportHub;

@Component({
   selector: 'app-my-profile-accounts',
   standalone: true,
   imports: [NgIf, NgForOf, RoleLabelPipe],
   templateUrl: './my-profile-accounts.component.html',
   styleUrls: ['./my-profile-accounts.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyProfileAccountsComponent implements OnDestroy {
   private auth = inject(AuthService);
   private propStore = inject(PropertyStoreService);
   private propService = inject(PropertyService);

   private destroy$ = new Subject<void>();

   isLoading = signal<boolean>(true);
   property = signal<Property | null>(null);
   me = signal<User | null>(null);
   accountOwnerUsers = signal<User[]>([]);

   supportHubLink = SUPPORT_HUB_LINK;

   private currentSub = '';

   userDisplayName = computed(() => {
      const u = this.me();
      if (!u) return '';
      return (
         u.fullName?.trim() ||
         `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
         u.username ||
         u.email ||
         ''
      );
   });

   accountOwnerNames = computed(() => {
      const users = this.accountOwnerUsers();
      if (!users || users.length === 0) return '';
      return users
         .map(
            (u) =>
               u.fullName?.trim() ||
               `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
               u.username ||
               u.email ||
               ''
         )
         .filter((name) => !!name)
         .join(', ');
   });

   myGroups = computed(() => (this.me()?.groups ?? []).filter((g) => !!g?.isActive));
   myRoles = computed(() => this.me()?.roles ?? []);

   constructor() {
      this.auth.user$
         .pipe(takeUntil(this.destroy$))
         .subscribe((u: Auth0User | null | undefined) => {
            this.currentSub = (u?.sub ?? '').trim();
            this.tryLoad();
         });

      this.propStore.currentProperty.pipe(takeUntil(this.destroy$)).subscribe((p) => {
         this.property.set(p ?? null);
         this.tryLoad();
      });
   }

   private tryLoad() {
      const prop = this.property();
      if (!prop) return;

      if (!this.currentSub) return;

      this.isLoading.set(true);

      this.propService.getUsers(prop.id).subscribe({
         next: (users: User[]) => {
            //TODO: Replace this with a call to UserStoreService currentUser. It will be set to the current user, and will be updated when the property changes.
            const me =
               users.find((u) => (u.authId ?? '').trim() === this.currentSub) ?? null;

            this.me.set(me);

            const owners =
               users.filter((u) =>
                  (u.roles ?? []).some(
                     (r) => String(r.id) === String(ACCOUNT_OWNER_ROLE_ID)
                  )
               ) ?? [];
            this.accountOwnerUsers.set(owners);

            this.isLoading.set(false);
         },
         error: () => {
            this.me.set(null);
            this.accountOwnerUsers.set([]);
            this.isLoading.set(false);
         }
      });
   }

   refresh(): void {
      this.tryLoad();
   }

   trackById = (_: number, x: { id: unknown }) => x.id as any;

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
